import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon, IconName } from './Icon';
import './curation.css';

export interface MenuItem {
  key: string;
  label: string;
  icon: IconName;
  danger?: boolean;
  separatorBefore?: boolean;
  /** Shown but not usable; the hint says why. */
  disabled?: boolean;
  hint?: string;
  onSelect: () => void;
}

export const KebabMenu: React.FC<{ label: string; items: MenuItem[]; triggerText?: string }> = ({ label, items, triggerText }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const button = useRef<HTMLButtonElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) button.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open || !button.current || !menu.current) return;
    const rect = button.current.getBoundingClientRect();
    const height = menu.current.offsetHeight;
    const width = menu.current.offsetWidth;
    let top = rect.bottom + 6;
    if (top + height > window.innerHeight - 8) top = rect.top - height - 6;
    setPosition({
      top: Math.max(8, top),
      left: Math.max(8, Math.min(window.innerWidth - width - 8, triggerText ? rect.left : rect.right - width)),
    });
    menu.current.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
  }, [open, triggerText]);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!menu.current?.contains(target) && !button.current?.contains(target)) close(false);
    };
    const onScroll = () => close(false);
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, close]);

  const onMenuKeyDown = (event: React.KeyboardEvent) => {
    const buttons = Array.from(menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') || []);
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close(true);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      buttons[(index + 1) % buttons.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      buttons[(index - 1 + buttons.length) % buttons.length]?.focus();
    } else if (event.key === 'Tab') {
      close(false);
    }
  };

  const toggle = (event: React.MouseEvent) => { event.stopPropagation(); setOpen((value) => !value); };
  return (
    <>
      {triggerText ? (
        <button ref={button} type="button" className="ui-btn ui-btn--secondary ui-btn--sm" aria-haspopup="menu"
          aria-expanded={open} onClick={toggle}>
          <Icon name="plus" size="sm" />{triggerText}
        </button>
      ) : (
        <button ref={button} type="button" className="cur-kebab" aria-haspopup="menu" aria-expanded={open} aria-label={label}
          onClick={toggle}>
          <Icon name="kebab" size="sm" />
        </button>
      )}
      {open && createPortal(
        <div className="cur">
          <div ref={menu} className="cur-menu" role="menu" aria-label={label} style={position} onKeyDown={onMenuKeyDown}
            onClick={(event) => event.stopPropagation()}>
            {items.map((item) => (
              <React.Fragment key={item.key}>
                {item.separatorBefore && <hr />}
                <button type="button" role="menuitem" aria-disabled={item.disabled ? 'true' : undefined}
                  className={item.danger && !item.disabled ? 'cur-danger' : undefined}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (item.disabled) return;
                    close(false);
                    item.onSelect();
                  }}>
                  <Icon name={item.icon} size="sm" />
                  {item.hint
                    ? <span className="cur-menu-label">{item.label}<span className="cur-menu-hint">{item.hint}</span></span>
                    : item.label}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};
